import {
  AuthenticatorProps,
  WithAuthenticator,
  WithAuthenticatorProps,
} from '@konecorp/ui-library';
import { Box, CssBaseline } from '@mui/material';
import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import Context from '../../context';
import LoadingSpinner from '../../components/LoadingSpinner';
import Header from '../../components/Header';
import EditForm from '../../components/EditForm';
import ListView from '../../components/ListView';
import { ImageManagement } from '../../components/ImageManagement';
import { ImageContextProvider } from '../../imageContext';
import ErrorMessage from '../../components/ErrorMessage';
import SuccessMessage from '../../components/SuccessMessage';
import ScrollToTop from '../../components/ScrollToTop';

const APP_URL = process.env.REACT_APP_IAPP_URL || '';
const RELEASE_DATE= process.env.REACT_APP_RELEASE_DATE ;
const REACT_APP_TEMPLATE_VERSION = `${process.env.REACT_APP_TEMPLATE_VERSION} - date ${RELEASE_DATE}` || '2.0.0';

const AdminApp = (
  props: PropsWithChildren<WithAuthenticatorProps>
): JSX.Element => {
  const {
    authState: { user, isAuthenticated },
    updateAcquireToken,
    updateAuthState,
  } = useContext(Context);

  const history = useNavigate();
  const location = useLocation();

  const redirectUrl = window.sessionStorage.getItem('redirectUrl');
  const [referrerUrl, setReferrerUrl] = useState(
    window.sessionStorage.getItem('referrerUrl')
  );

  useEffect(() => {
    const { pathname, search } = location;

    if (pathname.length > 1 && !isAuthenticated) {
      window.sessionStorage.setItem('redirectUrl', `${pathname}${search}`);
    }
    if (pathname.length === 1 && isAuthenticated) {
      if (redirectUrl) {
        history(redirectUrl);
      }
    }
    if (pathname.length > 1 && isAuthenticated) {
      window.sessionStorage.removeItem('redirectUrl');
    }
  }, [location, isAuthenticated, history]);

  useEffect(() => {
    const updateLoginData = async (): Promise<void> => {
      try {
        const authResponse = await props.acquireToken(true);
        updateAcquireToken(() => props.acquireToken); //store the function

        await props.updateGraphData(authResponse?.accessToken || '');

        const authState = props.getAuthState();

        updateAuthState(authState);
      } catch (error) {
        console.error('updateLoginData', error);
      }
    };

    if (
      !referrerUrl &&
      document.referrer &&
      process.env.REACT_APP_IAPP_URL !== document.referrer
    ) {
      const url = document.referrer;

      setReferrerUrl(url);
      window.sessionStorage.setItem('referrerUrl', url);
    }
    if (!isAuthenticated) updateLoginData();
  }, [isAuthenticated, referrerUrl, props, updateAuthState]);

  return (
    <>
      {!isAuthenticated && <LoadingSpinner/>}
      {isAuthenticated && (
        <>
          <Box display="flex" pb={11}>
            <CssBaseline />
            <Box
              sx={{
                flexGrow: 1,
                backgroundColor: '#0000',
                paddingBottom: '90px', // To account for footer space
              }}
            >
              <Box
                sx={{
                  backgroundColor: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  marginBottom: 2,
                }}
              >
                <Header userName={user?.username} version={REACT_APP_TEMPLATE_VERSION} />
                <ErrorMessage/>
                <SuccessMessage/>
                </Box>
                <ScrollToTop/>
              <Routes>
                <Route path="/" element={<ListView />} />
                <Route path="/create" element={<ImageContextProvider><EditForm /></ImageContextProvider>} />
                <Route path="/edit" element={<ImageContextProvider><EditForm /></ImageContextProvider>} />
                <Route path="/images" element={<ImageManagement />} />
              </Routes>
            </Box>
          </Box>
          <Box
            data-testid="footer"
            sx={{
              position: 'fixed',
              left: 0,
              bottom: 0,
              width: '100%',
              '& span.Mui-selected': {
                fontSize: 12,
              },
            }}
          >
            {/* Footer content */}
          </Box>
        </>
      )}
    </>
  );
};
export { AdminApp as PureComponent };

export default WithAuthenticator(AdminApp, {
  clientId: '92480ffe-d1fa-434d-96a5-a1a1d635d0a2',
  authority:
    'https://login.microsoftonline.com/2bb82c64-2eb1-43f7-8862-fdc1d2333b50',
  redirectUri: APP_URL,
  validateAuthority: true,
  postLogoutRedirectUri: APP_URL,
  navigateToLoginRequestUrl: false,
} as AuthenticatorProps);
